import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaFilter, FaCalendar, FaTag, FaStar, FaEye as FaViews } from 'react-icons/fa';
import './AdminManagement.css';
import { API_ENDPOINTS } from '../../config/api';

const WeeklyHighlightsManagement = () => {
  const navigate = useNavigate();
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [selectedHighlights, setSelectedHighlights] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'All',
    dateRange: '',
    tags: '',
    featured: false
  });

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.WEEKLY_HIGHLIGHTS);
      setHighlights(response.data);
    } catch (err) {
      console.error('Error fetching highlights:', err);
      setError('Failed to fetch highlights: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (highlightId) => {
    if (window.confirm('Are you sure you want to delete this highlight?')) {
      try {
        await axios.delete(`${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/${highlightId}`);
        fetchHighlights();
      } catch (err) {
        console.error('Delete error:', err);
        setError('Failed to delete highlight: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedHighlights.length === 0) {
      alert('Please select highlights first');
      return;
    }

    setBulkActionLoading(true);
    setError('');

    try {
      switch (action) {
        case 'publish':
          const publishResponse = await axios.post(`${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/bulk/publish`, {
            ids: selectedHighlights
          });
          alert(`Successfully published ${publishResponse.data.affectedRows} highlights`);
          break;
        case 'archive':
          const archiveResponse = await axios.post(`${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/bulk/archive`, {
            ids: selectedHighlights
          });
          alert(`Successfully archived ${archiveResponse.data.affectedRows} highlights`);
          break;
        case 'delete':
          if (window.confirm(`Are you sure you want to delete ${selectedHighlights.length} highlights?`)) {
            const deleteResponse = await axios.post(`${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/bulk/delete`, {
              ids: selectedHighlights
            });
            alert(`Successfully deleted ${deleteResponse.data.deletedCount} highlights`);
          } else {
            setBulkActionLoading(false);
            return; // User cancelled, don't clear selection
          }
          break;
        default:
          break;
      }
      
      setSelectedHighlights([]);
      fetchHighlights();
    } catch (err) {
      console.error('Bulk action error:', err);
      setError('Bulk action failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleEdit = (highlight) => {
    navigate(`/dashboard/weekly-highlights/edit/${highlight.custom_url || highlight.id}`);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('handleCreate called - navigating to create page');
    console.log('Current location:', window.location.href);
    console.log('Navigate function:', navigate);
    console.log('Event:', e);
    
    try {
      const result = navigate('/dashboard/weekly-highlights/create');
      console.log('Navigation result:', result);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const filteredHighlights = highlights.filter(highlight => {
    const matchesSearch = highlight.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         highlight.subtitle?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filters.status === 'All' || highlight.status === filters.status;
    const matchesFeatured = !filters.featured || highlight.featured;
    
    return matchesSearch && matchesStatus && matchesFeatured;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: '#6c757d', bg: '#f8f9fa' },
      published: { color: '#28a745', bg: '#d4edda' },
      archived: { color: '#dc3545', bg: '#f8d7da' },
      scheduled: { color: '#ffc107', bg: '#fff3cd' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        border: `1px solid ${config.color}`
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading weekly highlights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0">Weekly Highlights Management</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreate}
              onMouseDown={(e) => e.preventDefault()}
              style={{
                background: '#6366f1',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FaPlus /> Add New Highlight
            </button>
          </div>

          {/* Filters and Search */}
          <div className="card mb-4" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search highlights..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ border: '1px solid #dee2e6' }}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    style={{ border: '1px solid #dee2e6' }}
                  >
                    <option value="All">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="featuredFilter"
                      checked={filters.featured}
                      onChange={(e) => setFilters(prev => ({ ...prev, featured: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="featuredFilter">
                      Featured Only
                    </label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-success btn-sm"
                      onClick={() => handleBulkAction('publish')}
                      disabled={selectedHighlights.length === 0 || bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Publishing...
                        </>
                      ) : (
                        <>
                          <FaEye /> Publish Selected {selectedHighlights.length > 0 && `(${selectedHighlights.length})`}
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleBulkAction('archive')}
                      disabled={selectedHighlights.length === 0 || bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Archiving...
                        </>
                      ) : (
                        <>
                          <FaCalendar /> Archive Selected {selectedHighlights.length > 0 && `(${selectedHighlights.length})`}
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleBulkAction('delete')}
                      disabled={selectedHighlights.length === 0 || bulkActionLoading}
                    >
                      {bulkActionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <FaTrash /> Delete Selected {selectedHighlights.length > 0 && `(${selectedHighlights.length})`}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <strong>Error:</strong> {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Highlights Table */}
          <div className="card" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ border: 'none', padding: '1rem' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedHighlights(filteredHighlights.map(b => b.id));
                            } else {
                              setSelectedHighlights([]);
                            }
                          }}
                          checked={selectedHighlights.length === filteredHighlights.length && filteredHighlights.length > 0}
                        />
                      </th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Title</th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Week</th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Status</th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Updated</th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Views</th>
                      <th style={{ border: 'none', padding: '1rem', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHighlights.map((highlight) => (
                      <tr key={highlight.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                        <td style={{ padding: '1rem' }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedHighlights.includes(highlight.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedHighlights(prev => [...prev, highlight.id]);
                              } else {
                                setSelectedHighlights(prev => prev.filter(id => id !== highlight.id));
                              }
                            }}
                          />
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div>
                            <div className="fw-semibold" style={{ color: '#1f2937' }}>
                              {highlight.title}
                            </div>
                            {highlight.subtitle && (
                              <div className="text-muted small">{highlight.subtitle}</div>
                            )}
                            {highlight.featured && (
                              <span className="badge bg-warning text-dark ms-2">
                                <FaStar className="me-1" />
                                Featured
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div className="d-flex align-items-center">
                            <FaCalendar className="text-muted me-2" />
                            <span>{new Date(highlight.issue_date).toLocaleDateString()}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {getStatusBadge(highlight.status)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div className="text-muted small">
                            {new Date(highlight.updated_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div className="d-flex align-items-center">
                            <FaViews className="text-muted me-2" />
                            <span>{highlight.views || 0}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(highlight)}
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => window.open(`/weekly-highlights/${highlight.custom_url || highlight.id}`, '_blank')}
                              title="View"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(highlight.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* No Highlights Found */}
          {filteredHighlights.length === 0 && (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', color: 'rgba(0,0,0,0.1)', marginBottom: '1rem' }}>📰</div>
              <h3 className="text-muted mb-2">No Weekly Highlights Found</h3>
              <p className="text-muted">
                {searchTerm || Object.values(filters).some(f => f !== 'All' && f !== false)
                  ? 'Try adjusting your filters or search terms.'
                  : 'Create your first weekly highlight to get started.'
                }
              </p>
              {!searchTerm && Object.values(filters).every(f => f === 'All' || f === false) && (
                <button 
                  className="btn btn-primary"
                  onClick={handleCreate}
                >
                  Create Your First Highlight
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyHighlightsManagement; 
